import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, TableShell } from "@/components/EntityManager";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2, UserPlus, KeyRound, Pencil, Unlock, Lock, History, FileDown, Printer, Eye, EyeOff, Copy, MessageCircle } from "lucide-react";
import { fmtDate } from "@/lib/format";
import { printStaffLogins } from "@/lib/print";
import { toast } from "sonner";

const fmtDateTime = (iso) => {
  if (!iso) return "Never";
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch {
    return fmtDate(iso);
  }
};

import { BRANCHES as OFFICES } from "@/lib/branches";
const EMPTY = { name: "", email: "", user_id: "", password: "", phone: "", office: "Chennai", role: "staff", designation: "", staff_code: "" };

function LoginActivityDialog({ open, onClose }) {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    if (!open) return;
    setLogs(null);
    api.get("/login-activity").then((r) => setLogs(r.data || [])).catch(() => setLogs([]));
  }, [open]);
  const downloadExcel = () => {
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [
      ["Time", "Name", "User ID", "Email", "Branch", "Role", "Method", "IP"].map(esc).join(","),
      ...(logs || []).map((l) => [fmtDateTime(l.time), l.name || "", l.user_id || "", l.email || "", l.branch || "", l.role || "", l.method || "", l.ip || ""].map(esc).join(",")),
    ];
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "ntc-login-activity.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-[820px]" data-testid="login-activity-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Login Activity</DialogTitle>
          <DialogDescription>Recent sign-ins — who, when and from which branch.</DialogDescription>
        </DialogHeader>
        <div className="flex justify-end">
          <Button variant="outline" size="sm" onClick={downloadExcel} disabled={!logs?.length} data-testid="login-activity-excel-btn">
            <FileDown className="mr-1.5 h-4 w-4" /> Excel
          </Button>
        </div>
        {logs === null ? (
          <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
        ) : logs.length === 0 ? (
          <div className="py-8 text-center text-sm text-muted-foreground" data-testid="login-activity-empty">No sign-ins recorded yet. New logins will appear here.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>User ID</TableHead>
                <TableHead>Branch</TableHead>
                <TableHead>Method</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l) => (
                <TableRow key={l.id} data-testid="login-activity-row">
                  <TableCell className="whitespace-nowrap text-sm">{fmtDateTime(l.time)}</TableCell>
                  <TableCell className="text-sm font-medium">{l.name || "—"}</TableCell>
                  <TableCell className="text-sm">{l.user_id || l.email || "—"}</TableCell>
                  <TableCell className="text-sm">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${l.role === "admin" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-700"}`}>{l.branch || "—"}</span>
                  </TableCell>
                  <TableCell className={`text-xs ${(l.method || "").includes("REJECTED") ? "font-semibold text-red-600" : "text-muted-foreground"}`}>{l.method || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function StaffLogins() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [shownPw, setShownPw] = useState(() => new Set());
  const [q, setQ] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");

  const toggleRowSel = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const togglePw = (id) => setShownPw((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected = (rows || []).length > 0 && rows.every((u) => selected.has(u.id));
  const toggleAllSel = () => setSelected((p) => { const n = new Set(p); if (allSelected) rows.forEach((u) => n.delete(u.id)); else rows.forEach((u) => n.add(u.id)); return n; });
  const printSelected = () => {
    const list = (rows || []).filter((u) => selected.has(u.id));
    if (list.length === 0) { toast.info("Select at least one account to print"); return; }
    printStaffLogins(list);
  };

  const load = async () => {
    try {
      const r = await api.get("/users");
      setRows(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const relinkUser = async (u) => {
    const code = (u.user_id || u.name || "").trim();
    if (!code) return;
    if (!window.confirm(`Add "${u.name || code}" to NTC Staff Master with Staff Code "${code}" (Active)?`)) return;
    try {
      await api.post("/staff-master", {
        name: u.name || code,
        staff_code: code,
        branch: u.office || "Chennai",
        phone: u.phone || "",
        email: u.email || "",
        status: "Active",
      });
      toast.success(`${u.name || code} added to Staff Master — Staff Code "${code}" is now Active`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setForm({ name: u.name || "", email: u.email, user_id: u.user_id || "", password: "", phone: u.phone || "", office: u.office || "Chennai", role: u.role || "staff", designation: u.designation || "", staff_code: u.staff_code || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!editing && (!form.name || !form.email || !form.password)) {
      toast.error("Name, email and password are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, { name: form.name, office: form.office, role: form.role, user_id: form.user_id, phone: form.phone, designation: form.designation, staff_code: form.staff_code });
        toast.success("Account updated");
      } else {
        await api.post("/users", form);
        toast.success(`Staff login created for ${form.email}`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const isLocked = (u) => {
    if (!u.locked_until) return false;
    try { return new Date(u.locked_until) > new Date(); } catch { return false; }
  };

  const staleDays = (u) => {
    const ref = u.last_login || u.created_at;
    if (!ref) return null;
    try {
      const d = Math.floor((Date.now() - new Date(ref).getTime()) / 86400000);
      return d >= 30 ? d : null;
    } catch { return null; }
  };

  const unlock = async (u) => {
    try {
      await api.post(`/users/${u.id}/unlock`);
      toast.success(`${u.email} unlocked`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.put(`/users/${u.id}`, { active: !u.active });
      toast.success(!u.active ? "Account activated" : "Account deactivated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const doReset = async () => {
    if (newPw.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    try {
      await api.post(`/users/${pwUser.id}/reset-password`, { new_password: newPw });
      toast.success(`Password reset for ${pwUser.email}`);
      setPwOpen(false);
      setNewPw("");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete login for ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Account deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (me && me.role !== "admin") {
    return (
      <div>
        <PageHeader title="NTC Staff Logins" subtitle="Manage team accounts." />
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-8 text-center" data-testid="staff-not-authorized">
          <p className="font-medium text-amber-800">Not authorized</p>
          <p className="mt-1 text-sm text-amber-700">Only administrators can manage staff logins.</p>
        </div>
      </div>
    );
  }

  const shown = (rows || []).filter((u) => {
    if (branchFilter === "none" && u.office) return false;
    if (branchFilter !== "all" && branchFilter !== "none" && u.office !== branchFilter) return false;
    const needle = q.trim().toLowerCase();
    if (!needle) return true;
    return [u.name, u.user_id, u.email, u.phone, u.office, u.role, u.designation, u.staff_code]
      .some((x) => String(x || "").toLowerCase().includes(needle));
  });

  return (
    <div>
      <PageHeader
        title="NTC Staff Logins"
        subtitle="Create separate accounts for each office so your team doesn't share the admin login."
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Select value={branchFilter} onValueChange={setBranchFilter}>
              <SelectTrigger className="h-9 w-[160px]" data-testid="staff-branch-filter">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {OFFICES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                <SelectItem value="none">No Branch (—)</SelectItem>
              </SelectContent>
            </Select>
            <div className="relative">
              <Input
                className="h-9 w-[220px] pr-7"
                placeholder="Search name / ID / email / branch"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="staff-logins-search"
              />
              {q && (
                <button type="button" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" onClick={() => setQ("")} data-testid="staff-logins-search-clear">✕</button>
              )}
            </div>
            <Button variant="secondary" data-testid="print-staff-logins-btn" onClick={printSelected} disabled={selected.size === 0}>
              <Printer className="mr-2 h-4 w-4" /> Print{selected.size ? ` (${selected.size})` : ""}
            </Button>
            <Button variant="outline" data-testid="login-activity-btn" onClick={() => setActivityOpen(true)}>
              <History className="mr-2 h-4 w-4" /> Login Activity
            </Button>
            <Button data-testid="add-staff-btn" onClick={openCreate}>
              <Plus className="mr-2 h-4 w-4" /> New Staff Login
            </Button>
          </div>
        }
      />

      <LoginActivityDialog open={activityOpen} onClose={() => setActivityOpen(false)} />

      <TableShell>
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead className="w-10">
                <Checkbox checked={allSelected} onCheckedChange={toggleAllSel} data-testid="select-all-staff" aria-label="Select all accounts" />
              </TableHead>
              {["SL No.", "Branch", "User Name", "User ID", "Email Id", "Mobile No", "Password", "Role", "Designation", "Staff Code", "Last Login", "Status"].map((h) => (
                <TableHead key={h} className="text-[11px] uppercase tracking-[0.15em] text-slate-500">{h}</TableHead>
              ))}
              <TableHead className="w-[140px] text-right text-[11px] uppercase tracking-[0.15em] text-slate-500">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows === null &&
              [1, 2, 3].map((i) => (
                <TableRow key={i}>
                  {Array.from({ length: 13 }).map((_, j) => (
                    <TableCell key={j}><Skeleton className="h-4 w-24" /></TableCell>
                  ))}
                </TableRow>
              ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} className="py-12 text-center text-muted-foreground">No accounts yet. Create your first staff login.</TableCell>
              </TableRow>
            )}
            {rows && rows.length > 0 && shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={14} className="py-12 text-center text-muted-foreground">No accounts match "{q}".</TableCell>
              </TableRow>
            )}
            {rows &&
              shown.map((u, idx) => (
                <TableRow key={u.id} data-testid="staff-row" className={staleDays(u) && u.active !== false ? "bg-amber-50/70 hover:bg-amber-50" : undefined} data-state={selected.has(u.id) ? "selected" : undefined}>
                  <TableCell className="w-10">
                    <Checkbox checked={selected.has(u.id)} onCheckedChange={() => toggleRowSel(u.id)} data-testid="select-staff" aria-label="Select account" />
                  </TableCell>
                  <TableCell className="text-sm" data-testid="staff-sl-cell">{idx + 1}</TableCell>
                  <TableCell className="text-sm" data-testid="staff-branch-cell">{u.office || "—"}</TableCell>
                  <TableCell className="font-medium">
                    {u.name} {u.id === me?.id && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}
                  </TableCell>
                  <TableCell className="text-sm font-medium" data-testid="staff-user-id-cell">{u.user_id || "—"}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm" data-testid="staff-mobile-cell">{u.phone || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="staff-password-cell">
                    <span className="inline-flex items-center gap-1.5">
                      <span className="font-mono">{u.password_plain ? (shownPw.has(u.id) ? u.password_plain : "••••••••") : "—"}</span>
                      {u.password_plain && (
                        <button type="button" className="text-slate-400 hover:text-slate-700" title={shownPw.has(u.id) ? "Hide password" : "Show password"} onClick={() => togglePw(u.id)} data-testid="toggle-staff-password">
                          {shownPw.has(u.id) ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      {u.password_plain && (
                        <button
                          type="button"
                          className="text-slate-400 hover:text-primary"
                          title="Copy password"
                          data-testid="copy-staff-password"
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(u.password_plain);
                              toast.success(`Password copied for ${u.user_id || u.email}`);
                            } catch {
                              window.prompt("Copy password:", u.password_plain);
                            }
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">
                    <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-medium ${u.role === "admin" ? "border-primary/30 bg-primary/10 text-primary" : "border-slate-200 text-slate-600"}`}>
                      {u.role === "admin" ? "Admin" : u.role && u.role !== "staff" ? u.role : "Staff"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm" data-testid="designation-cell">{u.designation || "—"}</TableCell>
                  <TableCell className="text-sm" data-testid="staff-code-cell">
                    {u.staff_code ? (
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className="font-medium">{u.staff_code}</span>
                        <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${u.staff_code_status === "Active" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"}`} data-testid="staff-code-status-badge">
                          {u.staff_code_status}
                        </span>
                      </span>
                    ) : (
                      <span className="flex items-center gap-1.5 whitespace-nowrap">
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700" title="No matching Staff Code in NTC Staff Master — this user cannot sign in until one is added & Active" data-testid="staff-code-notlinked-badge">
                          Not in Staff Master
                        </span>
                        {(u.user_id || u.name) && (
                          <button
                            type="button"
                            className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 hover:bg-emerald-100"
                            title={`Create an Active Staff Master record with code "${u.user_id || u.name}" so this user can sign in`}
                            data-testid="add-to-staff-master-btn"
                            onClick={() => relinkUser(u)}
                          >
                            + Add to Staff Master
                          </button>
                        )}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm text-slate-600" data-testid="staff-last-login">
                    <span className="inline-flex items-center gap-1.5">
                      {fmtDateTime(u.last_login)}
                      {staleDays(u) && (
                        <span
                          className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800"
                          title={`No sign-in for ${staleDays(u)} days — consider disabling (toggle Status). Admin can re-activate anytime.`}
                          data-testid="staff-stale-badge"
                        >
                          Stale {staleDays(u)}d
                        </span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {isLocked(u) && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700" data-testid="staff-locked-badge">
                          <Lock className="h-3 w-3" /> Locked
                        </span>
                      )}
                      <Switch
                        checked={u.active !== false}
                        onCheckedChange={() => toggleActive(u)}
                        disabled={u.id === me?.id}
                        data-testid="staff-active-switch"
                      />
                      <span className={`text-xs ${u.active !== false ? "text-emerald-600" : "text-slate-400"}`}>
                        {u.active !== false ? "Active" : "Disabled"}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      {isLocked(u) && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-600" title="Unlock account" onClick={() => unlock(u)} data-testid="unlock-staff-btn">
                          <Unlock className="h-4 w-4" />
                        </Button>
                      )}
                      {u.password_plain && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-emerald-600"
                          title="Send login details on WhatsApp"
                          data-testid="whatsapp-staff-credentials-btn"
                          onClick={() => {
                            const digits = String(u.phone || "").replace(/\D/g, "");
                            const lines = [
                              `*NTC LOGIN DETAILS — Nisha Transport Corporation*`,
                              ``,
                              `User ID: *${u.user_id || u.email}*`,
                              `Password: *${u.password_plain}*`,
                              u.office ? `Branch: ${u.office}` : "",
                              ``,
                              `Login here: ${window.location.origin}`,
                              `Please keep these details private.`,
                            ].filter((l) => l !== "");
                            const msg = encodeURIComponent(lines.join("\n"));
                            window.open(digits ? `https://wa.me/${digits.length === 10 ? "91" + digits : digits}?text=${msg}` : `https://wa.me/?text=${msg}`, "_blank");
                          }}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" className="h-8 w-8" title="Edit" onClick={() => openEdit(u)} data-testid="edit-staff-btn">
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" title="Reset password" onClick={() => { setPwUser(u); setNewPw(""); setPwOpen(true); }} data-testid="reset-staff-pw-btn">
                        <KeyRound className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive hover:text-destructive" title="Delete" disabled={u.id === me?.id} onClick={() => remove(u)} data-testid="delete-staff-btn">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableShell>

      {/* Create / Edit dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[460px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <UserPlus className="h-4 w-4 text-primary" /> {editing ? "Edit Staff Login" : "New Staff Login"}
            </DialogTitle>
            <DialogDescription>
              {editing ? "Update this team member's details." : "Create a login for a team member and share the initial password with them."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-2">
            <div className="grid gap-1.5">
              <Label>Full name</Label>
              <Input data-testid="staff-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Ramesh Kumar" />
            </div>
            <div className="grid gap-1.5">
              <Label>Email (login ID)</Label>
              <Input type="email" data-testid="staff-email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="staff@ntclogistic.in" />
            </div>
            <div className="grid gap-1.5">
              <Label>User ID (for login)</Label>
              <Input data-testid="staff-user-id" value={form.user_id} onChange={(e) => setForm({ ...form, user_id: e.target.value })} placeholder="e.g. TADA OFFICE" />
            </div>
            <div className="grid gap-1.5">
              <Label>Mobile No. (for WhatsApp)</Label>
              <Input data-testid="staff-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="10-digit mobile" />
            </div>
            {!editing && (
              <div className="grid gap-1.5">
                <Label>Initial password</Label>
                <Input data-testid="staff-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters — share with staff" />
              </div>
            )}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Branch</Label>
                <Select value={form.office} onValueChange={(v) => setForm({ ...form, office: v })}>
                  <SelectTrigger data-testid="staff-office"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {OFFICES.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Role</Label>
                <Input
                  list="staff-role-options"
                  data-testid="staff-role"
                  value={form.role === "staff" ? "Staff" : form.role === "admin" ? "Admin" : form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  placeholder="e.g. Manager, Accountant"
                />
                <datalist id="staff-role-options">
                  <option value="Staff" />
                  <option value="Admin" />
                  <option value="Manager" />
                  <option value="Branch Manager" />
                  <option value="Accountant" />
                  <option value="Supervisor" />
                  <option value="MD" />
                </datalist>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-1.5">
                <Label>Designation</Label>
                <Input data-testid="staff-designation" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="e.g. Branch Incharge" />
              </div>
              <div className="grid gap-1.5">
                <Label>Staff Code</Label>
                <Input data-testid="staff-code-input" value={form.staff_code} onChange={(e) => setForm({ ...form, staff_code: e.target.value })} placeholder="e.g. NTC 006" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Type any role manually — only "Admin" gets full all-branch access; every other role has staff access. Staff Code is saved to NTC Staff Master as Active so the user can sign in.</p>
            {!editing && (
              <p className="text-xs text-muted-foreground">Staff currently have full access. Share the email and initial password with them; they can change it later from Settings.</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="save-staff-btn">
              {saving ? "Saving…" : editing ? "Save Changes" : "Create Login"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset password dialog */}
      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <KeyRound className="h-4 w-4 text-amber-600" /> Reset Password
            </DialogTitle>
            <DialogDescription>Set a new password for this account and share it with the user.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <p className="text-sm text-muted-foreground">Set a new password for <b>{pwUser?.email}</b> and share it with them.</p>
            <Input data-testid="staff-new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} placeholder="New password (min 6 characters)" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwOpen(false)}>Cancel</Button>
            <Button onClick={doReset} data-testid="confirm-reset-pw-btn">Reset Password</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
